"use client";

import { useLayoutEffect, useSyncExternalStore } from "react";

/* Single source of truth for the resolved theme: the `data-theme` attribute
   on <html>, written by the blocking inline script in the root layout (before
   first paint, so there's no flash) and by `setTheme` below. Everything here
   — including the CSS `light:`/`dark:` variants in globals.css — reads that
   one attribute, so this hook and the CSS can never disagree about which
   theme is actually active. Dark is the app's primary/default theme; light
   is opt-in via the navbar toggle only (no OS-preference following). */
export const THEME_STORAGE_KEY = "proxidize-theme";
const THEME_ATTR = "data-theme";

function applyTheme(resolved: "light" | "dark"): void {
  document.documentElement.setAttribute(THEME_ATTR, resolved);
}

/** Persists an explicit choice and applies it immediately — used by the navbar toggle. */
export function setTheme(next: "light" | "dark"): void {
  window.localStorage.setItem(THEME_STORAGE_KEY, next);
  applyTheme(next);
}

function subscribe(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: [THEME_ATTR] });
  return () => observer.disconnect();
}

function getSnapshot(): boolean {
  return document.documentElement.getAttribute(THEME_ATTR) === "light";
}

/* SSR renders the dark (default) theme; the client corrects on hydration
   only if the visitor previously toggled to light. */
function getServerSnapshot(): boolean {
  return false;
}

/** Whether the resolved theme is light — dark is the default, and light
 *  applies only once the visitor toggles it on (persisted from then on).
 *  The site theme is otherwise pure CSS — this hook exists for the few
 *  places that can't be themed with CSS alone (canvas drawings, iframe
 *  URLs, JS-built color values). Always `false` during SSR. */
export function usePrefersLight(): boolean {
  useLayoutEffect(() => {
    // Defensive re-sync, once per mount: an unrelated hydration mismatch
    // elsewhere on the page can make React re-touch <html> and silently drop
    // the data-theme attribute the blocking script set pre-hydration, even
    // though localStorage still holds the visitor's real choice. A layout
    // effect (not a passive one) runs synchronously right after that commit
    // and before the browser paints it, so the correction lands before the
    // visitor ever sees the dropped-back-to-dark frame instead of after it.
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark") {
      if (document.documentElement.getAttribute(THEME_ATTR) !== stored) applyTheme(stored);
    }
  }, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
